import { createI18n } from 'vue-i18n';
import en from './en.json';
import fr from './fr.json';
import ja from './ja.json';
import de from './de.json';
import es from './es.json';
import it from './it.json';
import pt from './pt.json';
import ru from './ru.json';
import tr from './tr.json';
import nl from './nl.json';
import sv from './sv.json';
import pl from './pl.json';
import ar from './ar.json';
import el from './el.json';
import cs from './cs.json';
import uk from './uk.json';
import ro from './ro.json';
import hi from './hi.json';

const SUPPORTED = ['en', 'fr', 'ja', 'de', 'es', 'it', 'pt', 'ru', 'tr', 'nl', 'sv', 'pl', 'ar', 'el', 'cs', 'uk', 'ro', 'hi'];
const RTL = ['ar'];

/** `source` merged into `target`, object by object; a string wins over
 *  what was there. */
function mergeMessages(target, source) {
  for (const [key, value] of Object.entries(source || {})) {
    const isTree = value && typeof value === 'object' && !Array.isArray(value);
    const hasTree = target[key] && typeof target[key] === 'object' && !Array.isArray(target[key]);
    if (isTree && hasTree) mergeMessages(target[key], value);
    else target[key] = value;
  }
  return target;
}

// A feature brings its strings in its own folder, one file per language
// (`features/<feature>/<lang>.json`, the same tree of keys as the files
// above), merged in here — so features added side by side never edit the
// same file. A string a language lacks falls back to English, as any does.
const messages = { en, fr, ja, de, es, it, pt, ru, tr, nl, sv, pl, ar, el, cs, uk, ro, hi };
const features = require.context('./features', true, /\.json$/);
features.keys().forEach((path) => {
  const lang = path.slice(path.lastIndexOf('/') + 1, -'.json'.length);
  if (!SUPPORTED.includes(lang)) return;
  const loaded = features(path);
  mergeMessages(messages[lang], loaded && loaded.__esModule ? loaded.default : loaded);
});

// Detect the user's browser language
function getBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0];

  if (SUPPORTED.includes(langCode)) {
    return langCode;
  }

  return 'en';
}

// Check for language in URL path (e.g. /fr, /es-es, /pt-pt)
function getLanguageFromPath() {
  const path = window.location.pathname;
  const segment = path.split('/')[1]?.toLowerCase() || '';
  if (!segment) return null;

  const base = segment.split('-')[0];
  if (SUPPORTED.includes(base)) return base;

  return null;
}

// Determine which language to use
function determineLanguage() {
  const pathLanguage = getLanguageFromPath();
  if (pathLanguage) return pathLanguage;

  const storedLang = localStorage.getItem('userLanguage');
  if (storedLang && SUPPORTED.includes(storedLang)) {
    return storedLang;
  }

  return getBrowserLanguage();
}

const initialLocale = determineLanguage();

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLocale,
  fallbackLocale: 'en',
  messages,
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
  document.documentElement.dir = RTL.includes(initialLocale) ? 'rtl' : 'ltr';
}

export default i18n;

export function setLanguage(lang) {
  if (SUPPORTED.includes(lang)) {
    localStorage.setItem('userLanguage', lang);
    i18n.global.locale.value = lang;
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.includes(lang) ? 'rtl' : 'ltr';
  }
}

export function getLanguageUrl(lang) {
  const baseUrl = 'https://wellnotes.ai';
  if (lang === 'en') return baseUrl;
  return `${baseUrl}/${lang}`;
}
